/**
 * Shared helpers for recovering from dynamic-import ("chunk load") failures.
 *
 * Every page is code-split into a content-hashed chunk that is fetched the
 * first time its route is visited. Two production conditions make that fetch
 * fail:
 *  1. A new deploy deleted the old hashed file a still-open tab references
 *     (stale deploy). Only a full reload onto the fresh shell recovers.
 *  2. A transient network blip while fetching the chunk (common on flaky
 *     mobile connections). A later reload also recovers.
 *
 * React Router surfaces both as a route error; `RouteErrorBoundary` reloads
 * once (throttled) to recover. These helpers centralise the detection and the
 * reload-throttle bookkeeping so the boundary stays thin and the logic is unit
 * testable.
 */

import type { ErrorEvent } from "@sentry/react";

// Matches the various browser/bundler phrasings for a failed dynamic import.
// `Importing a module script failed.` is the exact message Mobile Safari /
// iOS WKWebView reports (see Sentry ZENYT-PLATFORM-FRONTEND-PROD-2).
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|dynamically imported module|ChunkLoadError/i;

export const CHUNK_RELOAD_KEY = "chunk_reload_ts";
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

// First element of every chunk-load Sentry fingerprint. The second element
// (`recovered` | `stuck` | `uncaught`) records how the failure was handled.
export const CHUNK_LOAD_FINGERPRINT_KEY = "chunk-load-error";

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return CHUNK_ERROR_RE.test(message);
}

/**
 * Decide whether enough time has passed since the last recovery reload to
 * attempt another one. Reading the timestamp is wrapped in try/catch because
 * `sessionStorage` access throws in some sandboxed WebViews / private modes
 * (e.g. iOS WKWebView); when it throws we conservatively allow one reload
 * rather than leaving the user stuck.
 */
export function canReloadForChunkError(
  storage: Pick<Storage, "getItem"> | undefined,
  now: number = Date.now(),
  windowMs: number = CHUNK_RELOAD_WINDOW_MS,
): boolean {
  if (!storage) return true;
  let raw: string | null;
  try {
    raw = storage.getItem(CHUNK_RELOAD_KEY);
  } catch {
    return true;
  }
  const last = Number(raw ?? 0);
  if (!Number.isFinite(last)) return true;
  return now - last > windowMs;
}

/**
 * Stable Sentry grouping key for dynamic-import ("chunk load") failures.
 *
 * The browser's error message embeds the content-hashed chunk URL
 * (e.g. `assets/FindingsPage-VVQteNUe.js`), which changes on every deploy and
 * differs per route. Sentry's default grouping keys off that message (there is
 * no stack trace for these), so each self-healing stale-deploy reload spawns a
 * brand-new issue — and a "new issue" alert — every deploy
 * (e.g. ZENYT-PLATFORM-FRONTEND-STAGING-1). Pinning a deploy-stable fingerprint
 * collapses them into a single recurring issue.
 *
 * `recovered` (auto-reload in flight) and stuck (reload cooldown exhausted)
 * failures are split into two groups so the actionable "stuck" case stays
 * separate from the self-healing noise — mirroring the warning/error level
 * split the boundary already applies.
 */
export function chunkLoadFingerprint(recovered: boolean): string[] {
  return [CHUNK_LOAD_FINGERPRINT_KEY, recovered ? "recovered" : "stuck"];
}

/**
 * Whether an assembled Sentry event is a *self-healing* chunk-load failure that
 * `RouteErrorBoundary` already recovered from with an automatic reload.
 *
 * These carry the `["chunk-load-error", "recovered"]` fingerprint and are pure
 * stale-deploy noise: 0 user impact, the tab silently reloaded onto the fresh
 * build. Even reported at `warning` they reopen the grouped Sentry issue on
 * every deploy (the "regressed" churn behind ZENYT-PLATFORM-FRONTEND-STAGING-2),
 * so `processChunkLoadEvent` drops them. The genuinely actionable failures are
 * kept: `stuck` (the reload did NOT recover — the user is on the recovery
 * screen) and `uncaught` (a dynamic import that failed outside the router).
 */
export function isRecoveredChunkLoadEvent(event: ErrorEvent): boolean {
  const fingerprint = event.fingerprint;
  return (
    Array.isArray(fingerprint) &&
    fingerprint.length === 2 &&
    fingerprint[0] === CHUNK_LOAD_FINGERPRINT_KEY &&
    fingerprint[1] === "recovered"
  );
}

/**
 * Pull the strings off an assembled Sentry error event that could carry a
 * chunk-load signature: each exception's type + value, plus the top-level
 * message. (`Unhandled rejection` wrappers and some browsers surface the
 * phrasing on the message rather than the exception value.)
 */
function errorEventMessages(event: ErrorEvent): string[] {
  const messages: string[] = [];
  for (const value of event.exception?.values ?? []) {
    if (value.type) messages.push(value.type);
    if (value.value) messages.push(value.value);
  }
  if (typeof event.message === "string") messages.push(event.message);
  return messages;
}

/** Whether an assembled Sentry event describes a dynamic-import failure. */
export function isChunkLoadEvent(event: ErrorEvent): boolean {
  return errorEventMessages(event).some((message) => CHUNK_ERROR_RE.test(message));
}

/**
 * Sentry `beforeSend` safety net: pin the deploy-stable chunk-load fingerprint
 * on ANY dynamic-import failure that reaches Sentry without one.
 *
 * `RouteErrorBoundary` already fingerprints route-level chunk failures
 * (recovered/stuck), but it is only one capture path. A dynamic `import()`
 * outside the router (e.g. an on-demand vendor chunk), an error surfaced by the
 * top-level `Sentry.ErrorBoundary`, or any future lazy load can fail the same
 * way, bypass the boundary, and group by the content-hashed chunk URL embedded
 * in its message — spawning a brand-new Sentry issue (and "new issue" alert)
 * per deploy/route. Centralising the grouping here makes it independent of the
 * capture path, so this class of self-healing deploy noise can never silently
 * reappear through a path that forgot to set the fingerprint.
 *
 * Only acts when no fingerprint is set, so the boundary's richer
 * recovered/stuck split is preserved; events that already carry a fingerprint
 * (chunk-load or otherwise) pass through untouched.
 */
export function applyChunkLoadFingerprint(event: ErrorEvent): ErrorEvent {
  if (Array.isArray(event.fingerprint) && event.fingerprint.length > 0) {
    return event;
  }
  if (isChunkLoadEvent(event)) {
    event.fingerprint = [CHUNK_LOAD_FINGERPRINT_KEY, "uncaught"];
  }
  return event;
}

/**
 * Sentry `beforeSend` policy for dynamic-import ("chunk load") failures.
 * Centralises chunk-load handling so it is independent of the capture path.
 *
 *  1. Drop the self-healing `recovered` group (see `isRecoveredChunkLoadEvent`):
 *     the boundary already reloaded the tab onto the fresh build, so the event
 *     is pure stale-deploy noise that otherwise regresses the grouped issue on
 *     every deploy (ZENYT-PLATFORM-FRONTEND-STAGING-2). Returning `null`
 *     discards the event.
 *  2. Otherwise pin the deploy-stable fingerprint on any *ungrouped* chunk-load
 *     failure (see `applyChunkLoadFingerprint`). Already-grouped events — the
 *     actionable `stuck` group and the non-router `uncaught` group — pass
 *     through unchanged, as do all non-chunk errors.
 */
export function processChunkLoadEvent(event: ErrorEvent): ErrorEvent | null {
  if (isRecoveredChunkLoadEvent(event)) {
    return null;
  }
  return applyChunkLoadFingerprint(event);
}

/** Record the time of a recovery reload so the next one is throttled. */
export function recordChunkReload(
  storage: Pick<Storage, "setItem"> | undefined,
  now: number = Date.now(),
): void {
  if (!storage) return;
  try {
    storage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // sessionStorage unavailable (sandboxed WebView / private mode) — the
    // reload still happens, we just lose loop protection. Best effort.
  }
}
